import manifest from './asset-manifest.json'

export interface VasoAsset {
  source_drive_path: string
  local_path: string
  category: string
}

export const vasoAssets = manifest as VasoAsset[]

export function asset(relativePath: string): string {
  return `/assets/vaso/${relativePath.split('/').map(encodeURIComponent).join('/')}`
}

export function badgeAsset(relativePath: string): string {
  const webPath = relativePath
    .replace(/^勳章\//, '勳章-web/')
    .replace(/\.png$/i, '.webp')
  return asset(webPath)
}

export const characterAssets = {
  male: asset('家園/男生.png'),
  female: asset('家園/女生.png'),
  group: asset('家園/vaso_家園Q版人物.png'),
}

export const homeAssets = {
  background: asset('首頁/vaso_首頁底圖01.png'),
  alternateBackground: asset('首頁/vaso_首頁底圖.png'),
}

export const gardenStages = [
  { max: 19, label: '築夢起點', src: asset('家園/0-19%.png') },
  { max: 39, label: '溫暖萌芽', src: asset('家園/20-39%.png') },
  { max: 59, label: '安心成長', src: asset('家園/40-59%.png') },
  { max: 79, label: '幸福茁壯', src: asset('家園/60-79%.png') },
  { max: 99, label: '豐盛之家', src: asset('家園/80-99%.png') },
  { max: 100, label: '圓滿家園', src: asset('家園/100%.png') },
] as const

export const gratitudeAssets = {
  background: asset('vaso_感謝/vaso_感謝底圖.png'),
  warmth: asset('vaso_感謝/vaso_感謝_愛心圖樣.png'),
  wisdom: asset('vaso_感謝/vaso_感謝_燈泡圖樣.png'),
  reliability: asset('vaso_感謝/vaso_感謝_守護圖樣.png'),
  trophy: asset('vaso_感謝/左上獎盃.png'),
}

export const functionAssets = {
  banner: asset('功能中心/vaso_banner.png'),
  documents: asset('功能中心/01_file_center_文件中心.png'),
  knowledge: asset('功能中心/02_vaso_knowledge_VASO智庫.png'),
  promotion: asset('功能中心/03_promotion_application_升遷申請.png'),
  manager: asset('功能中心/04_manager_center_管理者中心.png'),
  account: asset('功能中心/05_account_settings_帳號設定.png'),
}

const excludedBadgeFragments = ['contact_sheet', '底圖', 'vaso_勳章.png', '模板']

const stationGradeConditions: Record<string, string> = {
  S: '可以獨立站崗、品質控管得宜，能協助其他崗位，並具有教育能力（能協助人員在兩週內完成考試）',
  A: '可以獨立站崗、品質控管得宜，且有餘力協助其他崗位',
  B: '可以獨立站崗，但偶爾仍需協助',
  C: '處於初學階段，仍需常態從旁協助',
}

function badgeRule(name: string, category: string): { condition: string; awardSource: string } {
  if (category.startsWith('個人站台')) {
    const grade = category.match(/([SABC])級/)?.[1]
    return {
      condition: grade ? `${grade}級：${stationGradeConditions[grade]}` : '依主管確認的崗位能力等級取得',
      awardSource: '主管評定崗位與等級後派發',
    }
  }

  const education = name.match(/^帶教 (\d+) 人 ([AS])級$/)
  if (category === '教育勳章' && education) {
    return {
      condition: `教育累積 ${education[1]} 名夥伴，完成一個崗位達 ${education[2]} 級`,
      awardSource: '門店主管依帶教成果派發',
    }
  }

  const namedReview = name.match(/^指名評論 (\d+) 次$/)
  if (category === '指名評論' && namedReview) {
    return {
      condition: `個人被顧客指名評論累積 ${namedReview[1]} 次`,
      awardSource: '主管登錄評論次數，系統累積並依門檻派發',
    }
  }

  const fiveStarReview = name.match(/^五星評論 (\d+) 則$/)
  if (category === '五星評論' && fiveStarReview) {
    return {
      condition: `所屬門店累積 5 星評論 ${fiveStarReview[1]} 則`,
      awardSource: '門店達標後由主管逐一派發',
    }
  }

  const dailyReview = name.match(/^評論達成 (\d+) 則$/)
  if (category === '評論數勳章' && dailyReview) {
    return {
      condition: `所屬門店當日評論數達 ${dailyReview[1]} 則`,
      awardSource: '門店達標後由主管逐一派發',
    }
  }

  const dailyCollection = name.match(/^當日餐評收集達\s*(\d+)\s*則$/)
  if (category === '餐評收集' && dailyCollection) {
    return {
      condition: `所屬門店當日餐評收集達 ${dailyCollection[1]} 則`,
      awardSource: '門店達標後由主管逐一派發',
    }
  }

  const consecutiveCollection = name.match(/^餐評收集連續\s*(\d+)(?:則|日)$/)
  if (category === '餐評收集' && consecutiveCollection) {
    return {
      condition: `所屬門店每日餐評收集達 ${consecutiveCollection[1]} 則，連續 10 日`,
      awardSource: '門店達標後由主管逐一派發',
    }
  }

  if (category === '團隊營業額勳章') {
    const conditions: Record<string, string> = {
      平日: '平日營業額達標：澄清 10 萬；楠梓、仁武、安平 7 萬',
      假日: '假日營業額達標：澄清 12 萬；楠梓、仁武、安平 10 萬',
      本月: '本月總營業額達標：澄清 300 萬；楠梓、安平 250 萬；仁武 220 萬',
    }
    return {
      condition: conditions[name] ?? '團隊營業額達到所屬門店與期間的指定門檻',
      awardSource: '門店達標後由主管逐一派發',
    }
  }

  return {
    condition: `${category}系列勳章；此素材未標示單一門檻`,
    awardSource: '依該系列已確認的派發規則',
  }
}

function badgeDisplayName(filename: string, category: string): string {
  const clean = filename.replace(/---.*$/, '')
  const count = clean.match(/(?:^|_)(20|50|70|80|90|100)(?:_|$)/)?.[1] ?? clean.match(/^(10|30)$/)?.[1]
  const stationNames: Record<string, string> = {
    baocan: '包餐', waichang: '外場', chengliao: '秤料', jiandan: '煎蛋', beiliao: '備料',
    tangqu: '湯區', caiqu: '菜區', zhengpan: '蒸盤', fantai: '飯區', guotai: '鍋台',
  }

  if (category === '五星評論') return `五星評論${count ? ` ${count} 則` : ''}`
  if (category === '評論數勳章') return `評論達成${count ? ` ${count} 則` : ''}`
  if (category === '指名評論') return /^\d+$/.test(clean) ? `指名評論 ${clean} 次` : '個人服務勳章'
  if (category === '教育勳章') {
    const match = clean.match(/^(\d)人([AS])$/)
    return match ? `帶教 ${match[1]} 人 ${match[2]}級` : '個人教育勳章'
  }
  if (category.startsWith('個人站台')) {
    const grade = category.match(/([SABC])級/)?.[1] ?? ''
    const transliterated = clean.match(/^\d+_([a-z]+)_[SABC]$/i)?.[1]?.toLowerCase()
    const station = transliterated ? stationNames[transliterated] : clean
    return `${station}${grade ? ` ${grade}級` : ''}`
  }
  if (category === '餐評收集') return clean.replace(/達 /g, '達').replace(/連續/g, '連續 ')
  if (/^[a-z0-9_-]+$/i.test(clean)) return category
  return clean
}

export const badgeAssets = vasoAssets
  .map((entry) => entry.local_path.replace('/assets/vaso/', ''))
  .filter((path) => path.startsWith('勳章/'))
  .filter((path) => !excludedBadgeFragments.some((fragment) => path.includes(fragment)))
  .map((path, index) => {
    const parts = path.split('/')
    const filename = parts.at(-1)?.replace(/\.png$/i, '') ?? `勳章 ${index + 1}`
    const category = parts.length > 2 ? parts[1] : '文化勳章'
    const name = badgeDisplayName(filename, category)
    return {
      id: `official-${index + 1}`,
      name,
      category,
      path,
      src: badgeAsset(path),
      earned: false,
      ...badgeRule(name, category),
    }
  })

export const cultureBadgeAssets = {
  warmth: gratitudeAssets.warmth,
  wisdom: gratitudeAssets.wisdom,
  reliability: gratitudeAssets.reliability,
  learning: badgeAssets.find((badge) => badge.category === '教育勳章')?.src ?? gratitudeAssets.wisdom,
  service: badgeAssets.find((badge) => badge.category === '指名評論')?.src ?? gratitudeAssets.reliability,
  culture: badgeAssets.find((badge) => badge.category === '團隊營業額勳章')?.src ?? gratitudeAssets.warmth,
} as const
